/**
 * 学習ログのクライアント状態機械（純粋関数のみ）。
 *
 * 08_client-state-machine.md の完全仕様をそのまま実装する。DOM・fetch・localStorage・Date.now() の
 * いずれにも触れない（design.md D4 / ADR-0007）。現在時刻は必ず引数 nowMs で受け取る。
 */

/** 学習ログ状態機械のフェーズ（用語集: PENDING_CHECK ほか）。 */
export type Phase = "PENDING_CHECK" | "AWAITING_CONFIRM" | "CONFIRMED_SILENT";

/** 送信の理由。ログ・テストの識別にのみ使い、リクエスト内容は理由によらず同一。 */
export type SendReason = "CONFIRM" | "SILENT" | "RETRY";

/**
 * 状態機械が要求する副作用。
 * トーストの表示・非表示は副作用ではなく isToastVisible() で導出する（§1 原則 5）。
 */
export type Effect = { readonly type: "FETCH_TODAY" } | { readonly type: "SEND_LOG"; readonly reason: SendReason };

/** localStorage に永続化する状態のすべて。すべて読み取り専用（更新は新しいオブジェクトを作る）。 */
export interface LearningLogState {
  readonly phase: Phase;
  readonly hasSucceededToday: boolean;
  /** PENDING_CHECK に入った時刻（エポックミリ秒）。他フェーズでは null */
  readonly pendingCheckStartedAt: number | null;
  readonly pendingGetDone: boolean;
  readonly pendingHasLogToday: boolean | null;
  /** 確認トーストを表示し始めた時刻。非表示中は null */
  readonly toastShownAt: number | null;
  /** 10 分未押下で自動非表示になった場合の再突入予定時刻 */
  readonly retryAt: number | null;
  /** CONFIRMED_SILENT の終了予定時刻 */
  readonly silentUntil: number | null;
  /** 送信の再送予定時刻。送信着手時に now + 1 分を入れ、成功で null に戻す */
  readonly confirmRetryAt: number | null;
  /** 直近に送信が成功した時刻 */
  readonly lastSentAt: number | null;
  /** 最後に日付リセットした JST 暦日（YYYY-MM-DD） */
  readonly lastResetDate: string;
}

/** decide() / onConfirmClicked() の戻り値。次の状態と、実行すべき副作用。 */
export interface Decision {
  readonly state: LearningLogState;
  readonly effects: readonly Effect[];
}

/** POST /api/learning-log のリクエストボディ（02_api-and-screens.md §3.1 と一致させる）。 */
export interface LearningLogPayload {
  readonly curriculumId: string;
  readonly activeHeading: string | null;
}

/** 周期チェッカーの間隔（design.md D7: 15 秒）。 */
export const TICK_INTERVAL_MS = 15_000;

/** PENDING_CHECK に入ってから GET /api/learning-log/today を呼ぶまで。 */
export const PENDING_GET_DELAY_MS = 60_000; // 1 分

/** PENDING_CHECK 開始からトースト表示まで（当日ログ「なし」のとき）。 */
export const PENDING_TOAST_DELAY_NO_LOG_MS = 12 * 60_000; // 12 分

/** PENDING_CHECK 開始からトースト表示まで（当日ログ「あり」のとき）。 */
export const PENDING_TOAST_DELAY_HAS_LOG_MS = 2 * 60_000; // 2 分

/** 確認トーストの最大表示時間。 */
export const TOAST_MAX_VISIBLE_MS = 10 * 60_000; // 10 分

/** 未押下で自動非表示になってから次の再突入までの待ち時間。 */
export const TOAST_RETRY_DELAY_MS = 60 * 60_000; // 60 分

/** CONFIRMED_SILENT の継続時間（「はい」による送信成功時刻から）。 */
export const SILENT_DURATION_MS = 90 * 60_000; // 90 分

/** CONFIRMED_SILENT 中のサイレント送信間隔。 */
export const SILENT_SEND_INTERVAL_MS = 10 * 60_000; // 10 分

/** 送信失敗時の再送間隔。 */
export const SEND_RETRY_DELAY_MS = 60_000; // 1 分

/** 日本標準時のオフセット（UTC+9）。 */
export const JST_OFFSET_MS = 9 * 60 * 60_000;

/** 確認トーストの文言（05_ui.md §3.3）。 */
export const TOAST_MESSAGE = "学習中ですか？";

/** decide() の内部遷移ループの上限（無限ループ防止）。 */
export const MAX_TRANSITIONS_PER_TICK = 8;

/** エポックミリ秒を JST の暦日文字列（YYYY-MM-DD）に変換する。 */
export function jstDateString(nowMs: number): string {
  const shifted = new Date(nowMs + JST_OFFSET_MS);
  return shifted.toISOString().slice(0, 10);
}

/** その日の初期状態を作る（PENDING_CHECK・hasSucceededToday = false）。 */
export function createInitialState(nowMs: number): LearningLogState {
  return {
    phase: "PENDING_CHECK",
    hasSucceededToday: false,
    pendingCheckStartedAt: nowMs,
    pendingGetDone: false,
    pendingHasLogToday: null,
    toastShownAt: null,
    retryAt: null,
    silentUntil: null,
    confirmRetryAt: null,
    lastSentAt: null,
    lastResetDate: jstDateString(nowMs),
  };
}

/** 状態が今日（JST）のものでなければ true。null は常に true。 */
export function isStale(state: LearningLogState | null, nowMs: number): boolean {
  if (state === null) return true;
  return state.lastResetDate !== jstDateString(nowMs);
}

interface StepResult {
  readonly next: LearningLogState;
  readonly effect: Effect | null;
}

/** 表 4.2 の T1〜T20 を上から評価し、最初にマッチした行だけを適用して返す。 */
function step(state: LearningLogState, now: number): StepResult {
  if (state.phase === "PENDING_CHECK") {
    if (state.pendingCheckStartedAt === null) {
      // T1: 不整合復旧
      return { next: { ...state, pendingCheckStartedAt: now }, effect: null };
    }
    const elapsed = now - state.pendingCheckStartedAt;
    if (!state.pendingGetDone) {
      if (elapsed >= PENDING_GET_DELAY_MS) {
        // T2
        return { next: state, effect: { type: "FETCH_TODAY" } };
      }
      // T3
      return { next: state, effect: null };
    }
    // pendingGetDone === true
    const threshold =
      state.pendingHasLogToday === true ? PENDING_TOAST_DELAY_HAS_LOG_MS : PENDING_TOAST_DELAY_NO_LOG_MS;
    if (elapsed >= threshold) {
      // T4 / T5
      return {
        next: {
          ...state,
          phase: "AWAITING_CONFIRM",
          toastShownAt: now,
          retryAt: null,
          pendingCheckStartedAt: null,
          pendingGetDone: false,
          pendingHasLogToday: null,
        },
        effect: null,
      };
    }
    // T6
    return { next: state, effect: null };
  }

  if (state.phase === "AWAITING_CONFIRM") {
    if (state.confirmRetryAt !== null) {
      if (now >= state.confirmRetryAt) {
        // T7
        return {
          next: { ...state, confirmRetryAt: now + SEND_RETRY_DELAY_MS },
          effect: { type: "SEND_LOG", reason: "RETRY" },
        };
      }
      // T8
      return { next: state, effect: null };
    }
    if (state.toastShownAt !== null) {
      if (now - state.toastShownAt >= TOAST_MAX_VISIBLE_MS) {
        // T9: toastShownAt（絶対時刻）+ 70 分を使う。now + 60 分ではない（§5.2 の注記）。
        return {
          next: {
            ...state,
            toastShownAt: null,
            retryAt: state.toastShownAt + TOAST_MAX_VISIBLE_MS + TOAST_RETRY_DELAY_MS,
          },
          effect: null,
        };
      }
      // T10: トースト表示中
      return { next: state, effect: null };
    }
    if (state.retryAt !== null) {
      if (now >= state.retryAt && state.hasSucceededToday) {
        // T11: GET を呼ばずに即再表示
        return { next: { ...state, toastShownAt: now, retryAt: null }, effect: null };
      }
      if (now >= state.retryAt && !state.hasSucceededToday) {
        // T12: PENDING_CHECK へ再突入
        return {
          next: {
            ...state,
            phase: "PENDING_CHECK",
            pendingCheckStartedAt: now,
            pendingGetDone: false,
            pendingHasLogToday: null,
            retryAt: null,
            toastShownAt: null,
          },
          effect: null,
        };
      }
      // T13
      return { next: state, effect: null };
    }
    // T14: confirmRetryAt / toastShownAt / retryAt がすべて null の不整合復旧
    return { next: { ...state, retryAt: now }, effect: null };
  }

  // state.phase === "CONFIRMED_SILENT"
  if (state.silentUntil === null) {
    // T16: 不整合復旧
    return { next: { ...state, silentUntil: now }, effect: null };
  }
  if (now >= state.silentUntil) {
    // T15: GET を呼ばずに即再表示。confirmRetryAt は破棄する（04_sequence.md 1-5）。
    return {
      next: {
        ...state,
        phase: "AWAITING_CONFIRM",
        toastShownAt: now,
        silentUntil: null,
        confirmRetryAt: null,
        retryAt: null,
      },
      effect: null,
    };
  }
  if (state.confirmRetryAt !== null) {
    if (now >= state.confirmRetryAt) {
      // T17
      return {
        next: { ...state, confirmRetryAt: now + SEND_RETRY_DELAY_MS },
        effect: { type: "SEND_LOG", reason: "RETRY" },
      };
    }
    // T18
    return { next: state, effect: null };
  }
  if (state.lastSentAt === null || now - state.lastSentAt >= SILENT_SEND_INTERVAL_MS) {
    // T19
    return {
      next: { ...state, confirmRetryAt: now + SEND_RETRY_DELAY_MS },
      effect: { type: "SEND_LOG", reason: "SILENT" },
    };
  }
  // T20
  return { next: state, effect: null };
}

/** 周期チェッカーが毎 tick 呼ぶ中心関数。次の状態と実行すべき副作用を返す。 */
export function decide(state: LearningLogState | null, nowMs: number): Decision {
  // isStale(state, now) は state === null のとき常に true を返すため、else 節では state が
  // 非 null であることが isStale の呼び出し結果から保証される。ただし TypeScript の型絞り込みの
  // ためにここでも null 判定を明示する。
  let current: LearningLogState =
    state === null || isStale(state, nowMs) ? createInitialState(nowMs) : state;
  const effects: Effect[] = [];

  for (let i = 0; i < MAX_TRANSITIONS_PER_TICK; i++) {
    const { next, effect } = step(current, nowMs);
    if (effect !== null) {
      effects.push(effect);
      current = next;
      break;
    }
    if (next === current) {
      break;
    }
    current = next;
  }

  return { state: current, effects };
}

/** 確認トーストを今この瞬間に表示すべきかを状態から導出する。 */
export function isToastVisible(state: LearningLogState, nowMs: number): boolean {
  return (
    state.phase === "AWAITING_CONFIRM" &&
    state.toastShownAt !== null &&
    nowMs - state.toastShownAt < TOAST_MAX_VISIBLE_MS
  );
}

/** 「はい」押下。トースト表示中でなければ何もしない。 */
export function onConfirmClicked(state: LearningLogState, nowMs: number): Decision {
  if (!isToastVisible(state, nowMs)) {
    // E2: 多重クリック・期限切れクリックの防御
    return { state, effects: [] };
  }
  return {
    state: {
      ...state,
      toastShownAt: null,
      retryAt: null,
      confirmRetryAt: nowMs + SEND_RETRY_DELAY_MS,
    },
    effects: [{ type: "SEND_LOG", reason: "CONFIRM" }],
  };
}

/** POST /api/learning-log が成功した（HTTP 2xx を受け取った）。 */
export function onSendSucceeded(state: LearningLogState, nowMs: number): LearningLogState {
  const silentUntil =
    state.phase === "CONFIRMED_SILENT" && state.silentUntil !== null
      ? state.silentUntil // サイレント送信の成功では期間を延長しない
      : nowMs + SILENT_DURATION_MS; // 「はい」による成功では 90 分を新規に開始する
  return {
    ...state,
    phase: "CONFIRMED_SILENT",
    hasSucceededToday: true,
    lastSentAt: nowMs,
    confirmRetryAt: null,
    toastShownAt: null,
    retryAt: null,
    pendingCheckStartedAt: null,
    pendingGetDone: false,
    pendingHasLogToday: null,
    silentUntil,
  };
}

/** POST /api/learning-log が失敗した（ネットワークエラー・2xx 以外）。 */
export function onSendFailed(state: LearningLogState, nowMs: number): LearningLogState {
  return { ...state, confirmRetryAt: nowMs + SEND_RETRY_DELAY_MS };
}

/** GET /api/learning-log/today の結果を反映する。取得失敗時は hasLogToday = false で呼ぶ。 */
export function onTodayFetched(state: LearningLogState, hasLogToday: boolean, _nowMs: number): LearningLogState {
  if (state.phase !== "PENDING_CHECK") return state; // E6: 遅延応答による汚染防止
  if (state.pendingGetDone) return state; // 二重反映防止
  if (state.pendingCheckStartedAt === null) return state; // 不整合
  return { ...state, pendingGetDone: true, pendingHasLogToday: hasLogToday };
}
